import { useAppTranslation } from "@/utils";
import { Button, Flex, InputNumber, LegacyCard, LegacyForm, Typography } from "@payping/web-components";
import { FC, useEffect, useState } from "react";
import { PlanData } from ".";
import { numberFormatter } from "@payping/utilities-shared/number";

export interface FormInputs {
  installmentCount: number;
}
type Props = {
  onNext: (value: number) => void;
  onPrev: () => void;
  planData: PlanData;
};
const InstallmentCount: FC<Props> = ({ onNext, onPrev, planData }) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FormInputs>();
  const [isFormValid, setIsFormValid] = useState(false);
  useEffect(() => {
    if (planData.installmentCount) {
      form.setFieldValue("installmentCount", planData.installmentCount);
      form.validateFields();
    }
  }, [planData, form]);
  return (
    <LegacyForm<FormInputs>
      form={form}
      layout="vertical"
      className="tw:w-full"
      onFieldsChange={() => {
        const hasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
        setIsFormValid(!hasErrors);
      }}
      onFinish={() => {
        const { installmentCount } = form.getFieldsValue();
        onNext(installmentCount);
      }}
    >
      <Flex vertical gap={"large"} className="tw:w-full tw:max-w-[545px] tw:max-md:max-w-[100%]">
        <Flex vertical gap={"small"}>
          <Typography.Title level={5} className="tw:m-0!">
            {t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.form.count.title")}
          </Typography.Title>
          <Typography.Text className="tw:m-0!">
            {t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.form.count.description")}
          </Typography.Text>
          <LegacyCard.Ant className="tw:mb-0! tw:mt-1! tw:max-h-64 tw:w-full">
            <LegacyForm.Item
              name={"installmentCount"}
              label={""}
              className="tw:mb-0!"
              rules={[
                { required: true },
                {
                  validator(_, value) {
                    if (value < 1) {
                      return Promise.reject(
                        new Error(
                          t("common.validations.min", {
                            name: t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.form.count.label"),
                            min: "1",
                          }),
                        ),
                      );
                    }
                    if (value > 36) {
                      return Promise.reject(
                        new Error(
                          t("common.validations.max", {
                            name: t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.form.count.label"),
                            max: "36",
                          }),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                parser={(value) => numberFormatter.parse(value)}
                className="tw:w-full! tw:text-center"
                style={{ textAlignLast: "center" }}
                inputMode="numeric"
                changeOnWheel={false}
                controls={false}
                step={1000}
                addonAfter={t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.month")}
              />
            </LegacyForm.Item>
          </LegacyCard.Ant>
        </Flex>
        <Flex gap={"small"}>
          <Button block={false} onClick={onPrev} className="tw:px-8!" loading={false} type="default">
            {t("common.previousStep")}
          </Button>
          <Button
            block={false}
            disabled={!isFormValid}
            onClick={form.submit}
            className="tw:px-8!"
            loading={false}
            type="primary"
          >
            {t("common.saveAndContinue")}
          </Button>
        </Flex>
      </Flex>
    </LegacyForm>
  );
};

export default InstallmentCount;
